import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import classes from "./ScrollHorizontal.module.css";

const ScrollHorizontal = forwardRef(({ dropClass, height, downDropStyle, children, dataStyle, onScrollFunc, showDots = false, className = "" }, ref) => {

    const innerRef = useRef(null);
    const scrollRef = ref || innerRef;
    const [dotCount, setDotCount] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);
    const [canScroll, setCanScroll] = useState(false);

    // Recalculates scrolling state and pagination.
    const refreshLayout = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const hasScroll = el.scrollWidth > el.clientWidth + 1;
        setCanScroll(hasScroll);
        setDotCount(hasScroll ? getPageCount(el) : 0);
        setActiveIndex(getActivePageIndex(el));
    }, [scrollRef]);

    // Updates the active page while scrolling.
    const handleScroll = (e) => {
        onScrollFunc?.(e);
        const el = scrollRef.current;
        if (!el) return;
        const nextIndex = getActivePageIndex(el);
        setActiveIndex(prevIndex => prevIndex === nextIndex ? prevIndex : nextIndex);
    };

    // Initial layout calculation and refresh when content changes.
    useEffect(() => {
        refreshLayout();
        const frame = requestAnimationFrame(refreshLayout);
        return () => cancelAnimationFrame(frame);
    }, [refreshLayout, children]);

    // Recalculate layout when the container size changes.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver(refreshLayout);
        observer.observe(el);
        return () => observer.disconnect();
    }, [scrollRef, refreshLayout]);

    // Returns the total number of scroll pages.
    const getPageCount = (el) => {
        if (!el || !el.clientWidth) return 0;
        return Math.ceil(el.scrollWidth / el.clientWidth);
    };
    // Returns the currently active page based on scroll position.
    const getActivePageIndex = (el) => {
        if (!el || !el.clientWidth) return 0;
        const pageCount = getPageCount(el);
        if (!pageCount) return 0;
        const maxScrollLeft = el.scrollWidth - el.clientWidth;
        if (maxScrollLeft <= 0) return 0;
        const rawScrollLeft = Math.abs(el.scrollLeft);
        const progress = rawScrollLeft / maxScrollLeft;
        const index = Math.round(progress * (pageCount - 1));
        return Math.max(0, Math.min(index, pageCount - 1));
    };

    return (
        <div className={`${classes.Wrapper} ${className}`}>
            <div ref={scrollRef} onScroll={handleScroll} style={{ height }} className={`${classes.Scroll} ${showDots && canScroll && dotCount > 1 ? classes.ScrollHideScrollbar : ""} ${dropClass || ""}`} data-style={dataStyle}>
                <div className={`${classes.Track} ${downDropStyle || ""}`}>
                    {children}
                </div>
            </div>
            {showDots && canScroll && dotCount > 1 &&
                <div className={classes.Dots} aria-hidden="true">
                    {Array.from({ length: dotCount }).map((_, index) => (
                        <span
                            key={index}
                            className={`
                                    ${classes.Dot}
                                    ${index === activeIndex ? classes.DotActive : ""}
                                `}
                        />
                    ))}
                </div>
            }
        </div>
    );
}
);

export default ScrollHorizontal;